import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Plus, Loader2, Trash2, Copy, Code, Eye, GripVertical, X } from "lucide-react";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;
const TYPES = ["text", "email", "phone", "textarea", "select", "rating", "number"];
const blank = () => ({ name: "", type: "form", fields: [{ type: "text", label: "Name", required: true }, { type: "email", label: "Email", required: true }], submit_text: "Submit", success_message: "Thanks! We'll be in touch shortly.", tag_on_submit: "" });

export default function Forms() {
  const { activeSubId } = useAuth();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!activeSubId) return;
    setLoading(true);
    api.get(`/forms?sub_account_id=${activeSubId}`).then(({ data }) => { setForms(data); setLoading(false); });
  }, [activeSubId]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const openEdit = (f) => { setEditing(f.id); setForm({ ...f, tag_on_submit: f.tag_on_submit || "" }); setOpen(true); };

  const setField = (i, patch) => setForm((f) => ({ ...f, fields: f.fields.map((x, k) => k === i ? { ...x, ...patch } : x) }));
  const addField = () => setForm((f) => ({ ...f, fields: [...f.fields, { type: "text", label: "New field", required: false }] }));
  const removeField = (i) => setForm((f) => ({ ...f, fields: f.fields.filter((_, k) => k !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, tag_on_submit: form.tag_on_submit || null };
      if (editing) await api.put(`/forms/${editing}`, payload);
      else await api.post(`/forms?sub_account_id=${activeSubId}`, payload);
      toast.success(editing ? "Form updated" : "Form created");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/forms/${id}`); toast.success("Form deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const copyLink = (id) => { navigator.clipboard?.writeText(`${BASE}/form/${id}`); toast.success("Public link copied"); };
  const copyEmbed = (id) => { navigator.clipboard?.writeText(`<iframe src="${BASE}/form/${id}" style="width:100%;max-width:560px;height:640px;border:0" title="Form"></iframe>`); toast.success("Embed snippet copied"); };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="forms-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Lead Capture</p>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mt-1">Forms & Surveys</h1>
        </div>
        <Button className="gap-2 font-semibold" onClick={openNew} data-testid="new-form-button"><Plus className="w-4 h-4" />New form</Button>
      </div>

      {forms.length === 0 ? (
        <Card className="p-16 text-center bg-card border-border/70" data-testid="forms-empty">
          <FileText className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No forms yet. Build one and embed it anywhere — submissions flow straight into your CRM.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {forms.map((f) => (
            <Card key={f.id} className="p-5 bg-card border-border/70 flex flex-col" data-testid={`form-${f.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center"><FileText className="w-5 h-5 text-primary" /></div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{f.name}</p>
                  <p className="text-xs text-muted-foreground">{f.fields?.length || 0} fields · {f.type}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 my-4 text-center">
                {[["Views", f.stats.views], ["Subs", f.stats.submissions], ["Rate", `${f.stats.rate}%`]].map(([l, v]) => (
                  <div key={l} className="rounded-lg border border-border/60 py-2"><p className="text-[10px] uppercase text-muted-foreground">{l}</p><p className="font-display font-bold">{v}</p></div>
                ))}
              </div>
              <div className="flex items-center gap-1 mt-auto">
                <Button size="sm" variant="outline" className="flex-1 gap-1.5" onClick={() => openEdit(f)} data-testid={`edit-form-${f.id}`}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => copyLink(f.id)} title="Copy link" data-testid={`link-form-${f.id}`}><Copy className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => copyEmbed(f.id)} title="Copy embed" data-testid={`embed-form-${f.id}`}><Code className="w-4 h-4" /></Button>
                <a href={`${BASE}/form/${f.id}`} target="_blank" rel="noreferrer" title="Preview"><Button size="sm" variant="ghost"><Eye className="w-4 h-4" /></Button></a>
                <Button size="sm" variant="ghost" onClick={() => del(f.id)} title="Delete" data-testid={`delete-form-${f.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit form" : "New form"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Contact us" data-testid="form-name-input" /></div>
            </div>
            <div className="space-y-2">
              <Label>Fields</Label>
              {form.fields.map((fl, i) => (
                <div key={i} className="flex items-center gap-2 rounded-lg border border-border/60 p-2" data-testid={`field-row-${i}`}>
                  <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
                  <Input value={fl.label} onChange={(e) => setField(i, { label: e.target.value })} className="h-8 flex-1" placeholder="Label" data-testid={`field-label-${i}`} />
                  <Select value={fl.type} onValueChange={(v) => setField(i, { type: v })}><SelectTrigger className="h-8 w-28 text-xs" data-testid={`field-type-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}</SelectContent></Select>
                  <button onClick={() => setField(i, { required: !fl.required })} className={`text-[10px] px-2 py-1 rounded ${fl.required ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`} data-testid={`field-required-${i}`}>req</button>
                  <button onClick={() => removeField(i)} className="text-muted-foreground hover:text-destructive"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addField} className="gap-1.5" data-testid="add-field-button"><Plus className="w-3.5 h-3.5" />Add field</Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Submit button</Label><Input value={form.submit_text} onChange={(e) => setForm({ ...form, submit_text: e.target.value })} data-testid="form-submit-text" /></div>
              <div className="space-y-1.5"><Label>Tag on submit</Label><Input value={form.tag_on_submit} onChange={(e) => setForm({ ...form, tag_on_submit: e.target.value })} placeholder="lead" data-testid="form-tag" /></div>
            </div>
            <div className="space-y-1.5"><Label>Success message</Label><Input value={form.success_message} onChange={(e) => setForm({ ...form, success_message: e.target.value })} data-testid="form-success" /></div>
          </div>
          <DialogFooter><Button onClick={save} disabled={saving || !form.name} className="gap-2" data-testid="save-form-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{editing ? "Save changes" : "Create form"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
